// This file is automatically generated by webgen from the struct descriptions in /generated-json/structs.
// Files in /generated-json are created from the structs configured in the 'main' function of /cmd/struct2json/main.go.
//
// DO NOT EDIT THIS FILE except where designated below.

/* eslint-disable no-relative-import-paths/no-relative-import-paths */

import type { PartialDeep } from 'type-fest';

import type { ExcludeMethodsDeep } from '../../../utils/types';
import { NIL_UUID } from '../../../utils/uuid';
import Enums from '../Enums';
import { type Market, type MarketWireFormat, NewMarket } from '../Market/Market';
import {
  NewParameterizedQuotePolicyThreshold,
  type ParameterizedQuotePolicyThreshold,
  type ParameterizedQuotePolicyThresholdWireFormat,
} from '../ParameterizedQuotePolicyThreshold/ParameterizedQuotePolicyThreshold';
import {
  NewQuotePolicyThreshold,
  type QuotePolicyThreshold,
  type QuotePolicyThresholdWireFormat,
} from '../QuotePolicyThreshold/QuotePolicyThreshold';
import { NewUser, type User, type UserWireFormat } from '../User/User';

export class QuotePolicy {
  readonly id: string;
  readonly name: string;
  readonly approvalLevel: number;
  readonly allMarkets: boolean;
  readonly allSalespeople: boolean;
  readonly markets: Market[];
  readonly reviewers: User[];
  readonly salespeople: User[];
  readonly thresholds: QuotePolicyThreshold[];
  readonly parameterizedThresholds: ParameterizedQuotePolicyThreshold[];
  readonly lowerTierPriceDeviationThreshold: string | null;
  readonly lowerTierPriceDeviationUnit: Enums.MarginConfiguration;
  readonly upperTierPriceDeviationThreshold: string | null;
  readonly upperTierPriceDeviationUnit: Enums.MarginConfiguration;

  constructor(data: { [key: string]: any } = {}) {
    this.id = data.id === undefined ? NIL_UUID : data.id;
    this.name = data.name === undefined ? '' : data.name;
    this.approvalLevel = data.approvalLevel === undefined ? 0 : data.approvalLevel;
    this.allMarkets = data.allMarkets === undefined ? false : data.allMarkets;
    this.allSalespeople = data.allSalespeople === undefined ? false : data.allSalespeople;
    this.markets = (data.markets ?? []).map((markets: any) => NewMarket(markets));
    this.reviewers = (data.reviewers ?? []).map((reviewers: any) => NewUser(reviewers));
    this.salespeople = (data.salespeople ?? []).map((salespeople: any) => NewUser(salespeople));
    this.thresholds = (data.thresholds ?? []).map((thresholds: any) =>
      NewQuotePolicyThreshold(thresholds),
    );
    this.parameterizedThresholds = (data.parameterizedThresholds ?? []).map(
      (parameterizedThresholds: any) =>
        NewParameterizedQuotePolicyThreshold(parameterizedThresholds),
    );
    this.lowerTierPriceDeviationThreshold =
      data.lowerTierPriceDeviationThreshold === undefined
        ? null
        : data.lowerTierPriceDeviationThreshold;
    this.lowerTierPriceDeviationUnit =
      data.lowerTierPriceDeviationUnit === undefined
        ? Enums.MarginConfiguration.Percentage
        : data.lowerTierPriceDeviationUnit;
    this.upperTierPriceDeviationThreshold =
      data.upperTierPriceDeviationThreshold === undefined
        ? null
        : data.upperTierPriceDeviationThreshold;
    this.upperTierPriceDeviationUnit =
      data.upperTierPriceDeviationUnit === undefined
        ? Enums.MarginConfiguration.Percentage
        : data.upperTierPriceDeviationUnit;
  }

  static zero(): QuotePolicy {
    const zeroValues: ExcludeMethodsDeep<QuotePolicy> = {
      id: NIL_UUID,
      name: '',
      approvalLevel: 0,
      allMarkets: false,
      allSalespeople: false,
      markets: [],
      reviewers: [],
      salespeople: [],
      thresholds: [],
      parameterizedThresholds: [],
      lowerTierPriceDeviationThreshold: null,
      lowerTierPriceDeviationUnit: Enums.MarginConfiguration.Percentage,
      upperTierPriceDeviationThreshold: null,
      upperTierPriceDeviationUnit: Enums.MarginConfiguration.Percentage,
    };
    return new QuotePolicy(zeroValues);
  }

  // ************* DO NOT EDIT ABOVE THIS LINE *************

  /**
   * reviewerNamesDisplay returns a string containing the full name of every
   * reviewer as a human readable list.
   */
  reviewerNamesDisplay(conjunction: string = 'or'): string {
    const reviewerNames = this.reviewers.map((r) => r.fullName()).sort();
    if (reviewerNames.length < 3) {
      return reviewerNames.join(` ${conjunction} `);
    }
    const allButLast = reviewerNames.slice(0, reviewerNames.length - 1);
    const last = reviewerNames[reviewerNames.length - 1];
    return `${allButLast.join(', ')}, ${conjunction} ${last}`;
  }

  /**
   * tierDeviationCriteria returns human-readable criteria for a policy's tier
   * price deviation configuration, one entry per enabled section.
   *
   * Attention: it is mirrored by tierDeviationDescriptionSQL (quote_policies.go) so change them in sync.
   */
  tierDeviationCriteria(): string[] {
    const format = (value: string, unit: Enums.MarginConfiguration): string =>
      unit === Enums.MarginConfiguration.FlatDollar ? `$${value}` : `${value}%`;

    const criteria: string[] = [];
    if (this.upperTierPriceDeviationThreshold !== null) {
      const amount = format(
        this.upperTierPriceDeviationThreshold,
        this.upperTierPriceDeviationUnit,
      );
      criteria.push(`Unit price must not be more than ${amount} above the tier price`);
    }
    if (this.lowerTierPriceDeviationThreshold !== null) {
      const amount = format(
        this.lowerTierPriceDeviationThreshold,
        this.lowerTierPriceDeviationUnit,
      );
      criteria.push(`Unit price must not be more than ${amount} below the tier price`);
    }
    return criteria;
  }

  // ************* DO NOT EDIT BELOW THIS LINE *************
}

export type QuotePolicyWireFormat = {
  id: string;
  name: string;
  approvalLevel: number;
  allMarkets: boolean;
  allSalespeople: boolean;
  markets: MarketWireFormat[];
  reviewers: UserWireFormat[];
  salespeople: UserWireFormat[];
  thresholds: QuotePolicyThresholdWireFormat[];
  parameterizedThresholds: ParameterizedQuotePolicyThresholdWireFormat[];
  lowerTierPriceDeviationThreshold: string | null;
  lowerTierPriceDeviationUnit: string;
  upperTierPriceDeviationThreshold: string | null;
  upperTierPriceDeviationUnit: string;
};

type NewQuotePolicyProps = PartialDeep<QuotePolicy, { recurseIntoArrays: true }>;
export const NewQuotePolicy: (props: NewQuotePolicyProps) => QuotePolicy = (props) =>
  new QuotePolicy(props);

type NewQuotePolicyFromWireFormatProps = PartialDeep<
  QuotePolicyWireFormat,
  { recurseIntoArrays: true }
>;
export const NewQuotePolicyFromWireFormat: (
  props: NewQuotePolicyFromWireFormatProps,
) => QuotePolicy = (props) => new QuotePolicy(props);
